import React, {useEffect, useState} from "react";
import {Link, NavLink, Outlet, useLocation, useNavigate} from "react-router-dom";
import SvgConsole from "../../svg/SvgConsole";
import {useLocalStorage} from "../../hooks/useLocalStorage";
import SvgLoader from "../../svg/SvgLoader";
import OverlayContext, {useOverlay} from "./overlay/OverlayContext";
import SocketContext, {useSocket} from "./SocketContext";
import ConsoleContext from "./console/ConsoleContext";
import PlayerContext, {usePlayers} from "./players/PlayerContext";
import ServerInfoContext, {useServerInfo} from "./ServerInfoContext";
import {useWatcher} from "../../hooks/useWatcher";
import {
    LucideChartLine,
    LucideDownload,
    LucideLayoutDashboard,
    LucideList, LucideLogs,
    LucideMap,
    LucideMessageCircle,
    LucideMousePointer2,
    LucidePersonStanding,
    LucideSettings,
    LucideShieldBan,
    LucideShieldCheck,
    LucideSword,
    LucideUser
} from "lucide-react";
import AuthContext, {useAuth} from "../home/contexts/AuthContext";
import ServerMenu from "./ServerMenu";
import {HubConnectionState} from "@microsoft/signalr";
import ChatContext from "./chat/ChatContext";
import UserSettingsContext from "./settings/UserSettingsContext";
import PWAUpdateNotification from "../../components/PWAUpdateNotification";
import ClientContext, {useClient} from "./ClientContext";
import PWAInstallNotification from "../../components/PWAInstallNotification";
import {getServiceWorkerVersion} from "../../util/pwaHelper";
import WhitelistContext from "./whitelist/WhitelistContext";
import AccountMenu from "../home/account/AccountMenu";

export default function Client(){

    useEffect(() => {
        getServiceWorkerVersion().then(v => console.log("SW Version:", v))
    })

    // Note: The innermost context is always evaluated first

    return <ClientContext>
        <SocketContext>
            <UserSettingsContext>
                <ConsoleContext>
                    <ChatContext>
                        <PlayerContext>
                            <WhitelistContext>
                                <ServerInfoContext>
                                    <AuthContext>
                                        <OverlayContext>
                                            <PWAInstallNotification />
                                            <PWAUpdateNotification />
                                            <ClientImpl />
                                        </OverlayContext>
                                    </AuthContext>
                                </ServerInfoContext>
                            </WhitelistContext>
                        </PlayerContext>
                    </ChatContext>
                </ConsoleContext>
            </UserSettingsContext>
        </SocketContext>
    </ClientContext>
}

function ClientImpl(){

    const client = useClient()
    const navigate = useNavigate()
    const auth = useAuth()

    const [showServerOverlay, setShowServerOverlay] = useState(false)

    const [showSidebar, setShowSidebar] = useState(false)

    const [lastLocation, setLastLocation] = useLocalStorage("lastLocation", false)

    const location = useLocation()

    useWatcher(location, () => {
        if (!auth.isLoggedIn || !auth.isValidServerId) {
            return
        }
        if (location.pathname === "/client" || location.pathname === "/client/") {
            navigate(lastLocation ?? "/client/dashboard")
        }
        else if (location.pathname.startsWith("/client")){
            setLastLocation(location.pathname)
        }
    }, true)

    useWatcher(auth, () => {
        if (auth.loaded){
            if (!auth.isLoggedIn){
                navigate("/login")
                return
            }

            if (auth.isValidServerId){
                setShowServerOverlay(false)
            }
            else {
                console.warn("AUTH: Invalid server id")
                setShowServerOverlay(true)
            }
        }
    })

    return (
        <>
            {!auth.loaded && <LoadingScreen/>}

            {!client.isStandalone && <nav className="fixed top-0 z-40 w-full bg-neutral-800">
                <div className="p-3 lg:px-5 lg:pl-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center justify-start rtl:justify-end">
                            <button type="button" onClick={() => setShowSidebar(!showSidebar)}
                                    className="inline-flex items-center p-2 text-sm text-neutral-500 rounded-lg sm:hidden hover:bg-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-800  :bg-neutral-700 :ring-neutral-600">
                                <span className="sr-only">Open sidebar</span>
                                <svg className="w-6 h-6" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path clipRule="evenodd" fillRule="evenodd"
                                          d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z"></path>
                                </svg>
                            </button>
                            <Link to="/" className="flex items-center relative ms-2">
                                <img src="/apple-touch-icon.png" className="rounded-md h-8 me-3"
                                     alt="Logo"/>
                                <span className="self-center text-xl font-semibold hidden sm:block sm:text-2xl whitespace-nowrap ">
                                    Rust Server Tool
                                </span>
                                <span className="ms-3 bg-primary text-white rounded-lg px-2 leading-normal hidden sm:block">
                                    BETA
                                </span>
                            </Link>
                        </div>
                        {!showServerOverlay && <ConnectionStatus />}
                        <AccountMenu />
                    </div>
                </div>
            </nav>}

            {!showServerOverlay && <aside
                className={`fixed top-0 left-0 z-30 ${!client.isStandalone ? "w-48" : ""} h-full ${client.isStandalone ? "pt-2" : "pt-20"} transition-transform ${showSidebar ? '' : '-translate-x-full'} bg-neutral-800 border-r border-neutral-900 sm:translate-x-0`}>
                <div className="h-full px-3 pb-4 overflow-y-auto bg-neutral-800 ">
                    <ul className="flex flex-col h-full space-y-2 font-medium">
                        <NavbarLink to="dashboard" displayName="Dashboard">
                            <LucideLayoutDashboard size={20}/>
                        </NavbarLink>
                        <NavbarLink to="players" displayName="Players">
                            <LucidePersonStanding size={20}/>
                        </NavbarLink>
                        <NavbarLink to="console" displayName="Console">
                            <SvgConsole size={20}/>
                        </NavbarLink>
                        <NavbarLink to="chat" displayName="Chat">
                            <LucideMessageCircle size={20}/>
                        </NavbarLink>
                        <NavbarLink to="map" displayName="Map">
                            <LucideMap size={20}/>
                        </NavbarLink>
                        <NavbarLink to="bans" displayName="Bans">
                            <LucideShieldBan size={20}/>
                        </NavbarLink>
                        <NavbarLink to="stats" displayName="Statistics">
                            <LucideChartLine size={20}/>
                        </NavbarLink>
                        <NavbarLink to="whitelist" displayName="Whitelist">
                            <LucideList size={20}/>
                        </NavbarLink>
                        <NavbarLink to="permissions" displayName="Permissions">
                            <LucideShieldCheck size={20}/>
                        </NavbarLink>
                        <NavbarLink to="moderators" displayName="Moderators">
                            <LucideSword size={20}/>
                        </NavbarLink>
                        {/*<NavbarLink to="log" displayName="Moderator Logs">*/}
                        {/*    <LucideLogs size={20}/>*/}
                        {/*</NavbarLink>*/}
                        <NavbarLink to="settings" displayName="Settings">
                            <LucideSettings size={20}/>
                        </NavbarLink>

                        <div className="flex-grow"></div>
                        <li>
                            <div className="flex items-center p-2 rounded-lg group hover:bg-neutral-900 cursor-pointer"
                                 onClick={() => setShowServerOverlay(true)}>
                                <LucideMousePointer2 size={20}/>
                                {!client.isStandalone && <span className="ms-3">Select Server</span>}
                            </div>
                        </li>
                        <NavbarLink to="/account" displayName="My Account">
                            <LucideUser size={20}/>
                        </NavbarLink>
                        {client.canInstall && <li>
                            <div
                                className="flex items-center p-2 rounded-lg group bg-green-700 hover:bg-green-700/90 cursor-pointer"
                                onClick={client.doInstall}>
                                <LucideDownload size={20}/>
                                {!client.isStandalone && <span className="ms-3">Install App</span>}
                            </div>
                        </li>}
                    </ul>
                </div>
            </aside>}

            <div className={`${!showServerOverlay ? client.isStandalone ? "sm:pl-16" : "sm:pl-48" : ""} ${client.isStandalone ? "" : "pt-[3.75em]"} h-screen`}>
                <div className="size-full relative">
                    {showServerOverlay
                        ? <ServerMenu hide={() => setShowServerOverlay(false)} />
                        : <Outlet />
                    }
                </div>
            </div>
        </>
    )

    function NavbarLink(props: { to: string, displayName?: string, children?: React.ReactElement | null }) {
        return (
            <li>
                <NavLink onClick={() => setShowSidebar(false)} to={props.to} className={({isActive}) =>
                    ((isActive ? "bg-primary hover:bg-primary-hover" : "bg-transparent hover:bg-neutral-900") + " flex items-center p-2 rounded-lg group")}>
                    {props.children}
                    {!client.isStandalone && <span className="ms-3">{props.displayName ?? props.to}</span>}
                </NavLink>
            </li>
        )
    }

    function ConnectionStatus(){
        const socket = useSocket()
        const serverInfo = useServerInfo()
        const players = usePlayers()
        const overlay = useOverlay()
        const status = socket.connection.current?.state ?? HubConnectionState.Disconnected

        const showDisableInfo = () => overlay.showAlert(
            "Server is disabled",
            "Your server is currently disabled. RustServerTool will not make any attempts to connect to your server.\n" +
            "If your server is offline for more than a day, RustServerTool will automatically disable it to save resources.\n " +
            "You can re-enable your server at any time on the settings page."
        )

        const showPluginOutdatedInfo = () => overlay.showAlert(
            "Plugin outdated",
            "The RustServerTool plugin running on your server is outdated. Some features may not work correctly until it is updated.\n" +
            "Please download and install the latest plugin version on your server."
        )

        useEffect(() => {
            const status = socket.connection.current?.state ?? HubConnectionState.Disconnected

            let title;
            if (status === HubConnectionState.Connected && serverInfo.displayName){
                title = `${serverInfo.serverDisabled ? ("⚫") : serverInfo.serverConnected ? ("🟢") : ("🔴")} ${serverInfo.displayName} - ${players.online.length} Online`
            }
            else if ((status === HubConnectionState.Connected && !serverInfo.displayName) || status === HubConnectionState.Connecting || status === HubConnectionState.Reconnecting) {
                if (serverInfo.displayName){
                    title = `🟡 ${serverInfo.displayName} - Connecting`
                }
                else{
                    title = `🟡 Connecting`
                }
            }
            else{
                if (serverInfo.displayName){
                    title = `⚠️ ${serverInfo.displayName} - No connection`
                }
                else{
                    title = `⚠️ No connection`
                }
            }

            if (!client.isStandalone){
                title += " - Rust Server Tool"
            }
            document.title = title
        }, [socket, serverInfo, players])

        return <div className="flex flex-row justify-center items-center">
            {serverInfo.serverDisabled && <button onClick={showDisableInfo} className="bg-red-500 text-white rounded-2xl py-2 px-4 mx-4 flex items-center gap-2">
                Server is disabled
                <span className="size-5 font-bold border rounded-full flex items-center justify-center">
                    ?
                </span>
            </button>}
            {serverInfo.pluginOutdated && <button onClick={showPluginOutdatedInfo} className="bg-red-500 text-white rounded-2xl py-2 px-4 mx-4 flex items-center gap-2">
                Plugin outdated
                <span className="size-5 font-bold border rounded-full flex items-center justify-center">
                    ?
                </span>
            </button>}
            <StatusElement/>
            <span className="block w-0.5 h-6 bg-neutral-600 mx-4 flex-grow-0 flex-shrink-0"></span>
            <span>{players.online.length} Online</span>
            {/*<span className="ms-2 text-sm font-mono text-neutral-400">{JSON.stringify(clientContext.serverInfo)}</span>*/}
        </div>

        function StatusElement(){
            if (status === HubConnectionState.Connected && serverInfo.displayName){
                return <>
                    <span className={"block rounded-full size-3 me-2 " + (serverInfo.serverDisabled ? "bg-neutral-500" : serverInfo.serverConnected ? "bg-green-500" : "bg-red-500")}></span>
                    {serverInfo.displayName}
                </>
            }
            if ((status === HubConnectionState.Connected && !serverInfo.displayName) || status === HubConnectionState.Connecting || status === HubConnectionState.Reconnecting) {
                return <>
                    <span className={"block rounded-full size-3 me-2 bg-yellow-500"}></span>
                    {serverInfo.displayName && serverInfo.displayName + " -"}&nbsp;<i>Connecting</i>
                </>
            }

            return <>
                <span className="me-2">&#9888;&#65039;</span>
                {serverInfo.displayName && serverInfo.displayName + " -"}&nbsp;<i>No connection</i>
                <button onClick={() => window.location.reload()}
                        className="text-blue-500 hover:underline bg-transparent border-none p-0 ms-2">Retry
                </button>
            </>
        }
    }
}

function LoadingScreen() {
    return (
        <div className="fixed inset-0 bg-neutral-800 flex items-center justify-center z-50">
            <div className="flex flex-col items-center">
                <SvgLoader />
                <p className="mt-4 text-white text-lg font-semibold tracking-wide">Loading...</p>
            </div>
        </div>
    );
}